import { create } from 'zustand';
import { newBlock, setProp, blockHasChildren } from './blockSchema.js';

// All block trees are arrays of nodes with optional `children` arrays.
// `__root__` is the synthetic parent id for the top-level array.

function findBlock(blocks, id) {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.children) {
      const found = findBlock(b.children, id);
      if (found) return found;
    }
  }
  return null;
}

// Find the parent id and index of a given block id. Returns
// { parentId: '__root__' | string, index: number } or null if not found.
function findLocation(blocks, id, parentId = '__root__') {
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].id === id) return { parentId, index: i };
    const children = blocks[i].children;
    if (children) {
      const inner = findLocation(children, id, blocks[i].id);
      if (inner) return inner;
    }
  }
  return null;
}

function findAndUpdate(blocks, id, updater) {
  return blocks.map((b) => {
    if (b.id === id) return updater(b);
    if (b.children) return { ...b, children: findAndUpdate(b.children, id, updater) };
    return b;
  });
}

function removeById(blocks, id) {
  const out = [];
  for (const b of blocks) {
    if (b.id === id) continue;
    if (b.children) {
      out.push({ ...b, children: removeById(b.children, id) });
    } else {
      out.push(b);
    }
  }
  return out;
}

function insertChild(blocks, parentId, child, index) {
  if (parentId === '__root__') {
    const arr = [...blocks];
    arr.splice(index ?? arr.length, 0, child);
    return arr;
  }
  return blocks.map((b) => {
    if (b.id === parentId) {
      const children = [...(b.children || [])];
      children.splice(index ?? children.length, 0, child);
      return { ...b, children };
    }
    if (b.children) return { ...b, children: insertChild(b.children, parentId, child, index) };
    return b;
  });
}

// Recursively clone a block subtree, assigning fresh ids at every level.
// Used by duplicateBlock so the duplicate doesn't share identity with the
// original — otherwise dnd-kit would treat both as the same sortable item.
function cloneWithFreshIds(block) {
  const next = {
    ...block,
    id: cryptoId(),
    props: structuredClone(block.props || {}),
  };
  if (block.children) {
    next.children = block.children.map(cloneWithFreshIds);
  }
  return next;
}

function cryptoId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'b_' + Math.random().toString(36).slice(2, 10);
}

// Returns true if `descendantId` is contained within (or is) `ancestorId`.
// Used to block dropping a block into itself.
function isDescendant(blocks, ancestorId, descendantId) {
  const ancestor = findBlock(blocks, ancestorId);
  if (!ancestor || !ancestor.children) return false;
  for (const c of ancestor.children) {
    if (c.id === descendantId) return true;
    if (c.children && isDescendant([c], c.id, descendantId)) return true;
  }
  return false;
}

const HISTORY_LIMIT = 50;

export const useEditor = create((set, get) => ({
  page: null,
  blocks: [],
  selectedId: null,
  device: 'desktop',
  past: [],
  future: [],
  dirty: false,
  saving: false,
  publishing: false,

  loadPage(page) {
    set({
      page,
      blocks: page.draft_blocks || page.blocks || [],
      selectedId: null,
      past: [],
      future: [],
      dirty: false,
    });
  },

  setDevice(device) {
    set({ device });
  },

  select(id) {
    set({ selectedId: id });
  },

  _commit(nextBlocks) {
    set((s) => ({
      past: [...s.past.slice(-HISTORY_LIMIT + 1), s.blocks],
      future: [],
      blocks: nextBlocks,
      dirty: true,
    }));
  },

  // Append a new block at the end of a parent (default: root).
  addBlock(type, parentId = '__root__', index = null) {
    const block = newBlock(type);
    const next = insertChild(get().blocks, parentId, block, index);
    get()._commit(next);
    set({ selectedId: block.id });
  },

  // Insert an already-shaped block at parent + index.
  insertBlock(block, parentId = '__root__', index = null) {
    const next = insertChild(get().blocks, parentId, block, index);
    get()._commit(next);
    set({ selectedId: block.id });
  },

  insertBlocks(blocks) {
    get()._commit([...get().blocks, ...blocks]);
  },

  replaceBlocks(blocks) {
    get()._commit(blocks);
  },

  updateBlockProps(id, key, value) {
    const next = findAndUpdate(get().blocks, id, (b) => setProp(b, key, value));
    get()._commit(next);
  },

  removeBlock(id) {
    const next = removeById(get().blocks, id);
    get()._commit(next);
    if (get().selectedId === id) set({ selectedId: null });
  },

  // Duplicate the given block immediately after itself, with fresh ids
  // throughout its subtree. Selects the new clone.
  duplicateBlock(id) {
    const blocks = get().blocks;
    const block = findBlock(blocks, id);
    const loc = findLocation(blocks, id);
    if (!block || !loc) return;
    const clone = cloneWithFreshIds(block);
    const next = insertChild(blocks, loc.parentId, clone, loc.index + 1);
    get()._commit(next);
    set({ selectedId: clone.id });
  },

  // Move a block up (-1) or down (+1) within its parent. Clamped to bounds.
  // Skips the shift compensation in `moveBlockTo` because the toolbar's
  // mental model is "swap with neighbor", not "drop on a sortable target".
  moveBlockBy(id, delta) {
    const blocks = get().blocks;
    const block = findBlock(blocks, id);
    const loc = findLocation(blocks, id);
    if (!block || !loc) return;

    const parentChildren = loc.parentId === '__root__'
      ? blocks
      : (findBlock(blocks, loc.parentId)?.children || []);
    const max = parentChildren.length - 1;
    const newIndex = Math.max(0, Math.min(max, loc.index + delta));
    if (newIndex === loc.index) return;

    const without = removeById(blocks, id);
    const next = insertChild(without, loc.parentId, block, newIndex);
    get()._commit(next);
  },

  // Move a block within or across containers. parentId is the target parent
  // ('__root__' for the top level). index is the position within that parent.
  moveBlockTo(activeId, parentId, index) {
    const blocks = get().blocks;
    const block = findBlock(blocks, activeId);
    if (!block) return;
    // Don't allow dropping a container into its own subtree.
    if (parentId !== '__root__' && (parentId === activeId || isDescendant(blocks, activeId, parentId))) {
      return;
    }

    const current = findLocation(blocks, activeId);
    if (!current) return;

    // If moving within the same parent and the target index is after the
    // source, removing the source first shifts the target index down by 1.
    let targetIndex = index;
    if (current.parentId === parentId && current.index < targetIndex) {
      targetIndex -= 1;
    }

    const without = removeById(blocks, activeId);
    const next = insertChild(without, parentId, block, targetIndex);
    get()._commit(next);
  },

  // Helper for tests / DnD overlay rendering.
  findLocation(id) {
    return findLocation(get().blocks, id);
  },

  acceptsChildren(type) {
    return blockHasChildren(type);
  },

  selectedBlock() {
    return findBlock(get().blocks, get().selectedId);
  },

  undo() {
    const { past, blocks, future } = get();
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    set({
      blocks: previous,
      past: past.slice(0, -1),
      future: [blocks, ...future].slice(0, HISTORY_LIMIT),
      dirty: true,
    });
  },

  redo() {
    const { past, blocks, future } = get();
    if (future.length === 0) return;
    const next = future[0];
    set({
      blocks: next,
      past: [...past, blocks].slice(-HISTORY_LIMIT),
      future: future.slice(1),
      dirty: true,
    });
  },

  markSaved() {
    set({ dirty: false });
  },

  setSaving(saving) {
    set({ saving });
  },

  setPublishing(publishing) {
    set({ publishing });
  },
}));
